import { useMemo, useCallback } from 'react';
import { useCurrency } from '@backpackjs/storefront';

import { getCustomerPrivacy } from '../Fueled/hooks/useCustomerPrivacy';

import {
  useDataLayerAccount,
  useDataLayerCart,
  useDataLayerCollection,
  useDataLayerCustomer,
  useDataLayerInit,
  useDataLayerProduct,
  useDataLayerSearch,
  useDataLayerSubscribe,
} from './hooks';

// Envs to set:
// * NEXT_PUBLIC_SITE_TITLE // provides backup brand name
// * NEXT_PUBLIC_ENABLE_DL_DEBUGGER // enables console logs for event dispatches
// * NEXT_PUBLIC_SHOPIFY_CUSTOM_APP_ID

const DEBUG = process.env.NEXT_PUBLIC_ENABLE_DL_DEBUGGER === 'true';

export function useDataLayerEventListeners({ pageTitle, template }) {
  const currencyCode = useCurrency();
  const baseEcommerce = useMemo(() => {
    if (!currencyCode) return null;
    return {
      currency_code: currencyCode,
      app_id: process.env.NEXT_PUBLIC_SHOPIFY_CUSTOM_APP_ID,
    };
  }, [currencyCode]);

  const customDispatchEvent = useCallback((eventName, eventData) => {
    const customerPrivacy = getCustomerPrivacy();
    if (customerPrivacy && !customerPrivacy.analyticsProcessingAllowed()) {
      return;
    }

    if (DEBUG) {
      console.log('Custom event dispatched:', eventName, eventData);
    }

    window.dispatchEvent(
      new CustomEvent(eventName, {
        detail: eventData,
      })
    );
  }, []);

  const { generateUserProperties, userProperties } = useDataLayerInit({
    baseEcommerce,
    DEBUG,
    pageTitle,
    template,
    customDispatchEvent,
  });

  const { userDataEvent, userDataEventTriggered } = useDataLayerCustomer({
    baseEcommerce,
    userProperties,
    customDispatchEvent,
  });

  useDataLayerAccount({
    baseEcommerce,
    generateUserProperties,
    userDataEvent,
    userDataEventTriggered,
    customDispatchEvent,
  });

  useDataLayerCart({
    baseEcommerce,
    userDataEvent,
    userDataEventTriggered,
    userProperties,
    customDispatchEvent,
  });

  useDataLayerProduct({
    baseEcommerce,
    userDataEvent,
    userProperties,
    customDispatchEvent,
  });

  useDataLayerCollection({
    baseEcommerce,
    userDataEvent,
    userDataEventTriggered,
    userProperties,
    customDispatchEvent,
  });

  useDataLayerSearch({
    baseEcommerce,
    userDataEvent,
    userDataEventTriggered,
    userProperties,
    customDispatchEvent,
  });

  useDataLayerSubscribe({
    baseEcommerce,
    userDataEventTriggered,
    customDispatchEvent,
  });
}
